import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { api, type StudentRecord } from '../lib/api';
import { Empty, PageHeading, Spinner } from '../components/ui';
import { AddForm, AddToggles, type Disclosure } from '../components/AddRecord';
import { CsvImport } from '../components/CsvImport';
import {
  Ledger,
  LedgerToolbar,
  onStage,
  searchRows,
  statusFacet,
  type LedgerColumn,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';
import { useIsPlatformAdmin } from '../lib/useSession';

const named = (student: StudentRecord) => `${student.lastName}, ${student.firstName}`;

/*
 * No Status column. The roll splits into On roll and Left as groups, so a
 * column repeating one of those two words on every row says nothing the
 * heading above it has not. A name that has left is struck through, the way a
 * retired period and an ended enrolment already are.
 *
 * No actions column either. The name is a link, which makes the whole row one
 * (see Ledger.tsx), and a Manage button beside it went to the same place.
 */
const COLUMNS: LedgerColumn<StudentRecord>[] = [
  {
    id: 'name',
    header: 'Name',
    sort: named,
    search: named,
    className: 'font-medium',
    cell: (student) => (
      <Link
        to="/manage/students/$studentId"
        params={{ studentId: student.id }}
        className={
          student.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
        }
      >
        {named(student)}
      </Link>
    ),
  },
  {
    id: 'guardian',
    header: 'Guardian',
    sort: (student) => student.guardianName,
    search: (student) => student.guardianName,
    className: 'text-ink-soft',
    cell: (student) => student.guardianName ?? '-',
  },
  {
    id: 'contact',
    header: 'Contact',
    sort: (student) => student.guardianContact,
    search: (student) => student.guardianContact,
    className: 'font-mono text-xs text-ink-soft',
    cell: (student) => student.guardianContact ?? '-',
  },
];

export function ManageStudents() {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<Disclosure>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianContact, setGuardianContact] = useState('');

  const search = useSearch({ from: '/app/manage/students' });
  const view = useListView({ search, columns: COLUMNS });
  const show = view.get('show');
  const isPlatformAdmin = useIsPlatformAdmin();
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const students = useQuery({ queryKey: ['students'], queryFn: api.admin.students });

  const create = useMutation({
    mutationFn: () =>
      api.admin.createStudent({
        firstName,
        lastName,
        guardianName: guardianName || null,
        guardianContact: guardianContact || null,
      }),
    onSuccess: async () => {
      setFirstName('');
      setLastName('');
      setGuardianName('');
      setGuardianContact('');
      setPanel(null);
      await queryClient.invalidateQueries({ queryKey: ['students'] });
    },
  });

  /*
   * An import can put students on rolls as well as on this list, so every
   * view of a roll is refetched too: each class page, its registers and the
   * class list's counts.
   */
  const imported = () =>
    Promise.all(
      [['students'], ['student'], ['classes'], ['enrollments'], ['class'], ['grid']].map(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      ),
    );

  const all = useMemo(() => students.data?.students ?? [], [students.data]);
  const rows = useMemo(
    () => searchRows(onStage(all, show), COLUMNS, view.query),
    [all, show, view.query],
  );

  return (
    <div>
      <PageHeading eyebrow="People on the roll" title="Students">
        <AddToggles panel={panel} onChange={setPanel} add="Add student" importable />
      </PageHeading>

      {panel === 'import' ? (
        <CsvImport entity="students" onImported={imported} onClose={() => setPanel(null)} />
      ) : null}

      {panel === 'add' ? (
        <AddForm
          error={create.error}
          fallback="Could not add student."
          pending={create.isPending}
          submit="Add student"
          onSubmit={() => create.mutate()}
        >
          <label className="grid gap-1.5">
            <span className="eyebrow">First name</span>
            <input
              className="field"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Last name</span>
            <input
              className="field"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Guardian</span>
            <input
              className="field"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Guardian contact</span>
            <input
              className="field"
              value={guardianContact}
              onChange={(e) => setGuardianContact(e.target.value)}
            />
          </label>
        </AddForm>
      ) : null}

      {students.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            facets={[
              statusFacet({
                label: 'Filter students by status',
                liveLabel: 'On roll',
                inactiveLabel: 'Left',
                value: show,
                onChange: (next) => view.set({ show: next || undefined }),
                live: onStage(all, '').length,
                inactive: onStage(all, 'inactive').length,
                deleted: isPlatformAdmin ? onStage(all, 'deleted').length : undefined,
              }),
            ]}
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="name, guardian or contact"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Students"
            rows={rows}
            total={all.length}
            columns={COLUMNS}
            rowId={(student) => student.id}
            sort={view.sort}
            onSort={view.onSort}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            empty={
              <Empty
                title="No students yet"
                hint="Add your first student, then enrol them in a class."
              />
            }
            noMatch={
              <Empty
                title="Nobody to show"
                hint={
                  view.query
                    ? 'Nobody here matches that name, guardian or contact.'
                    : show === 'deleted'
                      ? 'Nothing has been deleted here.'
                      : show === 'inactive'
                        ? 'Nobody here has left.'
                        : 'Everybody here has left. Switch to Left to see them.'
                }
              />
            }
          />
        </>
      )}
    </div>
  );
}
